import { useState } from 'react';
import {
  Plus,
  Trash2,
  Info,
  Check,
  X,
  Table2,
  List,
  KeyRound,
  MessageSquare,
  Target,
  Ban,
  Briefcase,
  Sparkles,
  ChevronDown,
  ChevronRight,
  FlaskConical,
  Play,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { Button, Badge } from '@centraoneplus/ui';
import { aiService, type ClassifyTicketTypeResult } from '@services/ai.service';
import { ThemedSelect } from '@components/common/ThemedSelect';

// ============================================================
// Types for Classification Config
// ============================================================

export interface FewShotExample {
  input: string;
  expectedType: boolean;
  explanation?: string;
}

export interface StructuralPattern {
  type: 'table' | 'list' | 'key_value';
  minColumns?: number;
  minItems?: number;
  requiredHeaders?: string[];
  itemPattern?: string;
  boostScore: number;
}

export interface ClassificationConfig {
  fewShotExamples?: FewShotExample[];
  confidenceThreshold?: number;
  structuralPatterns?: StructuralPattern[];
  intentKeywords?: string[];
  excludeKeywords?: string[];
  commercialIndicators?: string[];
  promptInstructions?: string;
  isCommercialType?: boolean;
}

interface ClassificationConfigEditorProps {
  config: ClassificationConfig;
  onChange: (config: ClassificationConfig) => void;
  isCatchAll: boolean;
  onIsCatchAllChange: (value: boolean) => void;
  /** Name of the ticket type being edited (used by the Test panel). */
  typeName?: string;
  /** Fallback keywords for the type (sent alongside config in the Test panel). */
  typeKeywords?: string[];
}

// ============================================================
// Helper Components
// ============================================================

function KeywordInput({
  label,
  description,
  keywords,
  onChange,
  icon: Icon,
  variant = 'default',
}: {
  label: string;
  description: string;
  keywords: string[];
  onChange: (keywords: string[]) => void;
  icon: any;
  variant?: 'default' | 'positive' | 'negative';
}) {
  const [input, setInput] = useState('');

  const addKeyword = () => {
    const trimmed = input.trim().toLowerCase();
    if (trimmed && !keywords.includes(trimmed)) {
      onChange([...keywords, trimmed]);
      setInput('');
    }
  };

  const removeKeyword = (index: number) => {
    const newKeywords = [...keywords];
    newKeywords.splice(index, 1);
    onChange(newKeywords);
  };

  const variantStyles = {
    default: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    positive: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    negative: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
      <div className="flex flex-wrap gap-2 mb-2">
        {keywords.map((keyword, index) => (
          <span
            key={index}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm ${variantStyles[variant]}`}
          >
            {keyword}
            <button
              type="button"
              onClick={() => removeKeyword(index)}
              className="text-current opacity-60 hover:opacity-100"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {keywords.length === 0 && (
          <span className="text-xs text-gray-400 dark:text-gray-500 italic">
            No keywords added
          </span>
        )}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addKeyword();
            }
          }}
          placeholder="Type a keyword and press Enter..."
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <Button type="button" variant="outline" size="sm" onClick={addKeyword}>
          Add
        </Button>
      </div>
    </div>
  );
}

function CollapsibleSection({
  title,
  icon: Icon,
  defaultOpen = false,
  badge,
  children,
}: {
  title: string;
  icon: any;
  defaultOpen?: boolean;
  badge?: string | number;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 p-4 text-left bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-500" />
        )}
        <Icon className="h-4 w-4 text-gray-500" />
        <span className="font-medium text-gray-900 dark:text-gray-100">{title}</span>
        {badge !== undefined && (
          <Badge variant="secondary" size="sm">
            {badge}
          </Badge>
        )}
      </button>
      {isOpen && (
        <div className="p-4 space-y-4 bg-white dark:bg-gray-900">{children}</div>
      )}
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================

export function ClassificationConfigEditor({
  config,
  onChange,
  isCatchAll,
  onIsCatchAllChange,
  typeName,
  typeKeywords,
}: ClassificationConfigEditorProps) {
  const updateConfig = <K extends keyof ClassificationConfig>(
    key: K,
    value: ClassificationConfig[K]
  ) => {
    onChange({ ...config, [key]: value });
  };

  // ── Test Classification panel state ──────────────────────────────────────
  const [testSubject, setTestSubject] = useState('');
  const [testBody, setTestBody] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<ClassifyTicketTypeResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const effectiveTypeName = (typeName || '').trim() || 'This type';

  const runTest = async () => {
    if (!testSubject.trim() && !testBody.trim()) return;
    setTesting(true);
    setTestError(null);
    setTestResult(null);
    try {
      // Classify the sample against THIS type (with the current, unsaved config)
      // plus a synthetic catch-all, so the result shows whether the config
      // actually captures the sample or it falls through to "Other".
      const looksHtml = /<[a-z][\s\S]*>/i.test(testBody);
      const result = await aiService.classifyTicketType({
        subject: testSubject.trim() || effectiveTypeName,
        description: testBody.trim() || testSubject.trim(),
        htmlContent: looksHtml ? testBody : undefined,
        ticketTypes: [
          {
            name: effectiveTypeName,
            keywords: typeKeywords || [],
            classificationConfig: config,
            isCatchAll,
          },
          { name: 'Other (catch-all)', classificationConfig: {}, isCatchAll: true },
        ],
      });
      setTestResult(result);
    } catch (e: any) {
      setTestError(
        e?.response?.data?.message || e?.message || 'Classification test failed. Is the AI engine running?',
      );
    } finally {
      setTesting(false);
    }
  };

  // ── Config summary (for the overview strip) ──────────────────────────────
  const exampleCount = config.fewShotExamples?.length || 0;
  const keywordCount =
    (config.intentKeywords?.length || 0) +
    (config.excludeKeywords?.length || 0) +
    (config.commercialIndicators?.length || 0);
  const patternCount = config.structuralPatterns?.length || 0;
  const matchedThisType =
    !!testResult &&
    (testResult.ticketTypeName || '').trim().toLowerCase() === effectiveTypeName.toLowerCase();

  // Few-shot examples management
  const addExample = () => {
    const examples = config.fewShotExamples || [];
    updateConfig('fewShotExamples', [
      ...examples,
      { input: '', expectedType: true, explanation: '' },
    ]);
  };

  const updateExample = (index: number, updates: Partial<FewShotExample>) => {
    const examples = [...(config.fewShotExamples || [])];
    examples[index] = { ...examples[index], ...updates };
    updateConfig('fewShotExamples', examples);
  };

  const removeExample = (index: number) => {
    const examples = [...(config.fewShotExamples || [])];
    examples.splice(index, 1);
    updateConfig('fewShotExamples', examples);
  };

  // Structural patterns management
  const addPattern = () => {
    const patterns = config.structuralPatterns || [];
    updateConfig('structuralPatterns', [
      ...patterns,
      { type: 'table', boostScore: 0.2 },
    ]);
  };

  const updatePattern = (index: number, updates: Partial<StructuralPattern>) => {
    const patterns = [...(config.structuralPatterns || [])];
    patterns[index] = { ...patterns[index], ...updates };
    updateConfig('structuralPatterns', patterns);
  };

  const removePattern = (index: number) => {
    const patterns = [...(config.structuralPatterns || [])];
    patterns.splice(index, 1);
    updateConfig('structuralPatterns', patterns);
  };

  return (
    <div className="space-y-6">
      {/* Intro */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-500 mt-0.5" />
          <div className="text-sm text-blue-700 dark:text-blue-300">
            <p className="font-medium mb-1">AI Classification Configuration</p>
            <p>
              Configure how the AI classifies tickets into this type. Add examples of what
              should and should not match, define keywords, and set structural patterns
              for better accuracy.
            </p>
          </div>
        </div>
      </div>

      {/* Configuration summary */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-gray-500 dark:text-gray-400">Configured signals:</span>
        <Badge variant="secondary" size="sm">{exampleCount} example{exampleCount !== 1 ? 's' : ''}</Badge>
        <Badge variant="secondary" size="sm">{keywordCount} keyword{keywordCount !== 1 ? 's' : ''}</Badge>
        <Badge variant="secondary" size="sm">{patternCount} pattern{patternCount !== 1 ? 's' : ''}</Badge>
        <Badge variant="secondary" size="sm">threshold {config.confidenceThreshold ?? 0.6}</Badge>
        {config.isCommercialType && <Badge variant="warning" size="sm">Commercial</Badge>}
        {isCatchAll && <Badge variant="info" size="sm">Catch-all</Badge>}
      </div>

      {/* Quick Settings */}
      <div className="grid grid-cols-2 gap-4">
        {/* Is Commercial Type */}
        <label className="flex items-center gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
          <input
            type="checkbox"
            checked={config.isCommercialType || false}
            onChange={(e) => updateConfig('isCommercialType', e.target.checked)}
            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <div>
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-gray-500" />
              <span className="font-medium text-gray-900 dark:text-gray-100">
                Commercial Type
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Requires explicit intent signals (quotes, orders, contracts)
            </p>
          </div>
        </label>

        {/* Is Catch-All */}
        <label className="flex items-center gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
          <input
            type="checkbox"
            checked={isCatchAll}
            onChange={(e) => onIsCatchAllChange(e.target.checked)}
            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <div>
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-gray-500" />
              <span className="font-medium text-gray-900 dark:text-gray-100">
                Catch-All Type
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Fallback for uncertain classifications
            </p>
          </div>
        </label>
      </div>

      {/* Confidence Threshold */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Confidence Threshold
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Minimum confidence required to classify as this type (0.0 - 1.0). Lower values
          accept more tickets, higher values are more strict.
        </p>
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={config.confidenceThreshold || 0.6}
          onChange={(e) =>
            updateConfig('confidenceThreshold', parseFloat(e.target.value))
          }
          className="w-full"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0.0 (Lenient)</span>
          <span className="font-medium text-primary-600">
            {config.confidenceThreshold || 0.6}
          </span>
          <span>1.0 (Strict)</span>
        </div>
      </div>

      {/* Few-Shot Examples */}
      <CollapsibleSection
        title="Training Examples"
        icon={Sparkles}
        defaultOpen
        badge={config.fewShotExamples?.length || 0}
      >
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Add examples of emails/tickets that should or should not be classified as this
          type. The AI learns from these examples to improve accuracy.
        </p>

        {(config.fewShotExamples || []).map((example, index) => (
          <div
            key={index}
            className={`p-4 rounded-lg border ${
              example.expectedType
                ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10'
                : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10'
            }`}
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex items-center gap-2">
                {example.expectedType ? (
                  <Badge variant="success" size="sm">
                    <Check className="h-3 w-3 mr-1" />
                    Should Match
                  </Badge>
                ) : (
                  <Badge variant="error" size="sm">
                    <X className="h-3 w-3 mr-1" />
                    Should NOT Match
                  </Badge>
                )}
                <button
                  type="button"
                  onClick={() =>
                    updateExample(index, { expectedType: !example.expectedType })
                  }
                  className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  Toggle
                </button>
              </div>
              <button
                type="button"
                onClick={() => removeExample(index)}
                className="text-gray-400 hover:text-red-500"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Example Input (email subject/body)
                </label>
                <textarea
                  value={example.input}
                  onChange={(e) => updateExample(index, { input: e.target.value })}
                  placeholder="e.g., Generate Quote for below MPANS: MPAN 1234567890..."
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Explanation (optional)
                </label>
                <input
                  type="text"
                  value={example.explanation || ''}
                  onChange={(e) => updateExample(index, { explanation: e.target.value })}
                  placeholder="e.g., Contains MPAN data and explicit quote request"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        ))}

        <Button type="button" variant="outline" size="sm" onClick={addExample}>
          <Plus className="h-4 w-4 mr-1" />
          Add Example
        </Button>
      </CollapsibleSection>

      {/* Keywords Section */}
      <CollapsibleSection
        title="Keywords"
        icon={KeyRound}
        badge={
          (config.intentKeywords?.length || 0) +
          (config.excludeKeywords?.length || 0) +
          (config.commercialIndicators?.length || 0)
        }
      >
        <div className="space-y-6">
          <KeywordInput
            label="Intent Keywords"
            description="Strong signals that indicate this ticket type (e.g., 'please quote', 'send pricing')"
            keywords={config.intentKeywords || []}
            onChange={(keywords) => updateConfig('intentKeywords', keywords)}
            icon={Target}
            variant="positive"
          />

          <KeywordInput
            label="Exclude Keywords"
            description="Signals that indicate this is NOT this ticket type (e.g., 'what is', 'explain')"
            keywords={config.excludeKeywords || []}
            onChange={(keywords) => updateConfig('excludeKeywords', keywords)}
            icon={Ban}
            variant="negative"
          />

          <KeywordInput
            label="Industry/Domain Terms"
            description="Industry-specific terms that indicate this type (e.g., 'mpan', 'sku', 'po#')"
            keywords={config.commercialIndicators || []}
            onChange={(keywords) => updateConfig('commercialIndicators', keywords)}
            icon={Briefcase}
          />
        </div>
      </CollapsibleSection>

      {/* Structural Patterns */}
      <CollapsibleSection
        title="Structural Patterns (Advanced)"
        icon={Table2}
        badge={config.structuralPatterns?.length || 0}
      >
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Detect tables, lists, or key-value pairs in emails. When detected, the
          confidence for this type is boosted.
        </p>

        {(config.structuralPatterns || []).map((pattern, index) => (
          <div
            key={index}
            className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                {pattern.type === 'table' && <Table2 className="h-4 w-4 text-gray-500" />}
                {pattern.type === 'list' && <List className="h-4 w-4 text-gray-500" />}
                {pattern.type === 'key_value' && (
                  <KeyRound className="h-4 w-4 text-gray-500" />
                )}
                <span className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                  {pattern.type.replace('_', ' ')} Pattern
                </span>
              </div>
              <button
                type="button"
                onClick={() => removePattern(index)}
                className="text-gray-400 hover:text-red-500"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Pattern Type
                </label>
                <ThemedSelect
                  value={pattern.type}
                  onChange={(e) =>
                    updatePattern(index, {
                      type: e.target.value as StructuralPattern['type'],
                    })
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="table">Table</option>
                  <option value="list">List</option>
                  <option value="key_value">Key-Value Pairs</option>
                </ThemedSelect>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Confidence Boost
                </label>
                <input
                  type="number"
                  min="0"
                  max="0.5"
                  step="0.05"
                  value={pattern.boostScore}
                  onChange={(e) =>
                    updatePattern(index, { boostScore: parseFloat(e.target.value) })
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>

              {pattern.type === 'table' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Min Columns
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={pattern.minColumns || 2}
                      onChange={(e) =>
                        updatePattern(index, { minColumns: parseInt(e.target.value) })
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Required Headers (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={(pattern.requiredHeaders || []).join(', ')}
                      onChange={(e) =>
                        updatePattern(index, {
                          requiredHeaders: e.target.value
                            .split(',')
                            .map((h) => h.trim())
                            .filter(Boolean),
                        })
                      }
                      placeholder="e.g., MPAN, EAC, Start Date"
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </>
              )}

              {pattern.type === 'list' && (
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Min Items
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={pattern.minItems || 2}
                    onChange={(e) =>
                      updatePattern(index, { minItems: parseInt(e.target.value) })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                </div>
              )}
            </div>
          </div>
        ))}

        <Button type="button" variant="outline" size="sm" onClick={addPattern}>
          <Plus className="h-4 w-4 mr-1" />
          Add Pattern
        </Button>
      </CollapsibleSection>

      {/* Custom Prompt Instructions */}
      <CollapsibleSection title="Custom Instructions" icon={MessageSquare}>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Add custom instructions for the AI when classifying this type. These will be
          included in the classification prompt.
        </p>
        <textarea
          value={config.promptInstructions || ''}
          onChange={(e) => updateConfig('promptInstructions', e.target.value)}
          placeholder="e.g., Look for MPANs in tables. Multiple MPANs = bulk quote request. Single MPAN with meter reading = support query."
          rows={4}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </CollapsibleSection>

      {/* Test Classification */}
      <div className="border border-purple-200 dark:border-purple-800 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 p-3 bg-purple-50 dark:bg-purple-900/20 flex-wrap">
          <FlaskConical className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          <span className="font-medium text-gray-900 dark:text-gray-100">Test classification</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Paste a sample email to see whether it matches &ldquo;{effectiveTypeName}&rdquo;
          </span>
        </div>
        <div className="p-4 space-y-3 bg-white dark:bg-gray-900">
          <input
            type="text"
            value={testSubject}
            onChange={(e) => setTestSubject(e.target.value)}
            placeholder="Sample subject (e.g. Quote request - new supply)"
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <textarea
            value={testBody}
            onChange={(e) => setTestBody(e.target.value)}
            placeholder="Sample email body (e.g. Please provide a quote for MPAN 1100018415022, EAC 1000, start date 25/05/2026...)"
            rows={4}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Uses your current (unsaved) settings above.
            </p>
            <Button
              type="button"
              size="sm"
              onClick={runTest}
              disabled={testing || (!testSubject.trim() && !testBody.trim())}
            >
              {testing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-1" />
                  Run test
                </>
              )}
            </Button>
          </div>

          {testError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{testError}</span>
            </div>
          )}

          {testResult && (
            <div
              className={`rounded-lg border p-3 ${
                matchedThisType
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                  : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
              }`}
            >
              <div className="flex items-center gap-2">
                {matchedThisType ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                )}
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {matchedThisType
                    ? `Matches “${effectiveTypeName}”`
                    : `Would be classified as “${testResult.ticketTypeName ?? 'unknown'}”`}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Confidence {Math.round((testResult.confidence ?? 0) * 100)}% · method{' '}
                {testResult.classificationMethod}
                {!matchedThisType &&
                  ' — add intent keywords / training examples above to capture this sample.'}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
